import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, Presentation, User, Calendar, Gift, Star } from 'lucide-react';
import servicesData from '../content/services.json';

const iconMap = {
  Users: Users,
  Presentation: Presentation,
  User: User,
  Calendar: Calendar,
  Gift: Gift,
  Star: Star,
};

const ServicesSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { sectionTitle, description, services: servicesDataList } = servicesData;

  const services = servicesDataList.map(service => ({
    ...service,
    icon: React.createElement(iconMap[service.icon] || Star, { size: 40 }),
    bgColor: service.gradient.includes('mint') ? 'bg-nitzan-mint/10' :
             service.gradient.includes('teal') ? 'bg-nitzan-teal/10' :
             service.gradient.includes('sage') ? 'bg-nitzan-sage/10' :
             'bg-nitzan-terracotta/10'
  }));

  return (
    <section id="services" className="section-padding bg-white">
      <div className="container-width">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Star className="text-nitzan-teal" size={20} />
            <span className="text-nitzan-teal font-medium">השירותים שלי</span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">{sectionTitle}</span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Link key={index} to={service.link}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 * index, duration: 0.6 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-nitzan-sage/20 cursor-pointer"
              >
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                {service.icon}
              </div>

              <h3 className="text-2xl font-bold text-nitzan-teal mb-4 group-hover:text-nitzan-mint transition-colors">
                {service.title}
              </h3>

              <p className="text-gray-700 leading-relaxed mb-6">
                {service.description}
              </p>

              <div className="flex items-center gap-2 text-nitzan-teal font-semibold group-hover:gap-4 transition-all">
                <span>קראי עוד</span>
                <svg
                  className="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </div>

              {/* Decorative corner */}
              <div className={`absolute top-4 left-4 w-16 h-16 rounded-full ${service.bgColor} -z-10 blur-2xl group-hover:scale-150 transition-transform duration-300`}></div>
              </motion.div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="text-center mt-16 bg-gradient-to-r from-nitzan-sage/20 via-nitzan-mint/20 to-nitzan-teal/20 rounded-3xl p-12"
        >
          <h3 className="text-3xl font-bold text-nitzan-teal mb-4">לא בטוחה מה מתאים לך?</h3>
          <p className="text-xl text-gray-700 mb-6">
            בואי נדבר ונמצא ביחד את הדרך הכי טובה עבורך
          </p>
          <Link to="/contact">
            <motion.div
              className="inline-block bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              צרי איתי קשר
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;

